import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RutasService } from '../services/rutasService';

const containerStyle = { width: '100%', height: '400px', borderRadius: '12px' };

// Geocodifica una dirección con Nominatim (OSM)
async function geocodeAddress(address) {
  try {
    // Validar que la dirección no esté vacía
    if (!address || address.trim().length === 0) {
      throw new Error('Dirección vacía');
    }

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address.trim())}&limit=1&addressdetails=1`;
    const res = await fetch(url, { 
      headers: { 
        'Accept-Language': 'es',
        'User-Agent': 'LaHuertaApp/1.0'
      } 
    });
    
    if (!res.ok) {
      throw new Error(`Error HTTP: ${res.status}`);
    }

    const data = await res.json();
    
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Dirección no encontrada');
    }

    const result = data[0];
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Validar que las coordenadas sean válidas
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      throw new Error('Coordenadas inválidas');
    }

    return { lat, lng };
  } catch (error) {
    console.error('Error en geocodificación:', error.message, 'para dirección:', address);
    throw new Error(`No se pudo encontrar la dirección: ${address}. ${error.message}`);
  }
}

// Llama al servicio TRIP de OSRM para optimizar el orden de paradas
async function osrmTrip(coords) {
  try {
    // Validar que hay al menos 2 coordenadas (origen + al menos 1 destino)
    if (!coords || coords.length < 2) {
      throw new Error('Se necesitan al menos 2 puntos para calcular una ruta');
    }

    // Validar que todas las coordenadas sean válidas
    for (const coord of coords) {
      if (!coord || typeof coord.lat !== 'number' || typeof coord.lng !== 'number' ||
          isNaN(coord.lat) || isNaN(coord.lng) ||
          coord.lat < -90 || coord.lat > 90 || coord.lng < -180 || coord.lng > 180) {
        throw new Error('Coordenadas inválidas en los puntos de la ruta');
      }
    }

    const coordsStr = coords.map(c => `${c.lng},${c.lat}`).join(';');
    const url = `https://router.project-osrm.org/trip/v1/driving/${coordsStr}?source=first&roundtrip=false&overview=full&geometries=geojson`;
    
    const res = await fetch(url, {
      headers: {
        'User-Agent': 'LaHuertaApp/1.0'
      }
    });
    
    if (!res.ok) {
      if (res.status === 400) {
        throw new Error('Solicitud inválida al servicio de rutas');
      } else if (res.status === 429) {
        throw new Error('Demasiadas solicitudes al servicio de rutas. Intenta más tarde.');
      } else if (res.status >= 500) {
        throw new Error('Servicio de rutas temporalmente no disponible');
      } else {
        throw new Error(`Error del servicio de rutas: ${res.status}`);
      }
    }

    const json = await res.json();
    
    if (!json.trips || json.trips.length === 0) {
      throw new Error('No se pudo calcular una ruta válida para los puntos dados');
    }

    if (json.code && json.code !== 'Ok') {
      throw new Error(`Error en el cálculo de ruta: ${json.message || json.code}`);
    }

    return json;
  } catch (error) {
    console.error('Error en OSRM:', error.message);
    throw new Error(`Error al calcular la ruta optimizada: ${error.message}`);
  }
}

export default function MapaRutas({ pedidosPendientes, origenTexto, origenCoords, onClose, modo = 'completo', onRutaGuardada, autoCalcular = false }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const markersRef = useRef([]);
  const [listaOptimizada, setListaOptimizada] = useState([]);
  const [rutaGeoJSON, setRutaGeoJSON] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [estadoCalculo, setEstadoCalculo] = useState('');
  const center = useMemo(() => ({ lat: origenCoords?.lat || 20.6736, lng: origenCoords?.lng || -103.344 } ), [origenCoords]);

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(mapContainerRef.current).setView([center.lat, center.lng], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    mapRef.current = map;
  }, [center]);

  useEffect(() => {
    if (autoCalcular) {
      calcularRuta();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCalcular]);

  function limpiarCapa() {
    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
      routeLayerRef.current = null;
    }
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
  }

  async function calcularRuta() {
    if (!mapRef.current) {
      alert('El mapa no está inicializado. Intenta recargar la página.');
      return;
    }
    
    setCalculando(true);
    setEstadoCalculo('Iniciando cálculo de ruta...');
    limpiarCapa();
    
    try {
      // Validar que hay pedidos pendientes
      const pedidos = (pedidosPendientes || []).filter(p => (p.direccion || '').trim().length > 0);
      
      if (pedidos.length === 0) {
        setListaOptimizada([]);
        setRutaGeoJSON(null);
        alert('No hay pedidos con direcciones válidas para calcular la ruta.\n\nRegistra pedidos con direcciones completas (calle, colonia, código postal) o verifica que las direcciones existan.');
        return;
      }

      // Obtener coordenadas del origen
      setEstadoCalculo('Obteniendo ubicación del origen...');
      let origen;
      try {
        if (origenTexto) {
          origen = await geocodeAddress(origenTexto);
        } else if (origenCoords) {
          origen = origenCoords;
        } else {
          origen = center;
        }
      } catch (error) {
        throw new Error(`Error al obtener la ubicación del origen: ${error.message}`);
      }

      // Geocodificar direcciones de destino
      setEstadoCalculo(`Geocodificando ${pedidos.length} direcciones...`);
      const destinos = [];
      const erroresGeocodificacion = [];
      
      for (let i = 0; i < pedidos.length; i++) {
        const p = pedidos[i];
        setEstadoCalculo(`Geocodificando dirección ${i + 1} de ${pedidos.length}...`);
        try {
          const g = await geocodeAddress(p.direccion);
          if (g) {
            destinos.push({ ...g, pedido: p });
          }
        } catch (error) {
          erroresGeocodificacion.push(`• ${p.direccion}: ${error.message}`);
        }
      }

      // Mostrar errores de geocodificación si los hay
      if (erroresGeocodificacion.length > 0) {
        const mensaje = `Se encontraron problemas con ${erroresGeocodificacion.length} dirección(es):\n\n${erroresGeocodificacion.join('\n')}\n\n¿Deseas continuar con las direcciones válidas?`;
        if (!window.confirm(mensaje)) {
          setCalculando(false);
          return;
        }
      }

      if (destinos.length === 0) {
        setListaOptimizada([]);
        setRutaGeoJSON(null);
        alert('No se pudo geocodificar ninguna dirección válida.\n\nVerifica que las direcciones estén completas y sean correctas.');
        return;
      }

      // Calcular ruta optimizada
      setEstadoCalculo('Calculando ruta optimizada...');
      const coords = [origen, ...destinos.map(d => ({ lat: d.lat, lng: d.lng }))];
      const osrm = await osrmTrip(coords);
      const trip = osrm.trips[0];
      
      if (!trip || !trip.waypoints) {
        throw new Error('La respuesta del servicio de rutas no contiene waypoints válidos');
      }

      const order = trip.waypoints
        .filter(w => w.waypoint_index > 0)
        .sort((a, b) => a.trips_index - b.trips_index)
        .map((w, i) => ({
          orden: i + 1,
          pedidoId: destinos[w.waypoint_index - 1].pedido.pedidoId || destinos[w.waypoint_index - 1].pedido.id,
          direccion: destinos[w.waypoint_index - 1].pedido.direccion
        }));

      setEstadoCalculo('Dibujando ruta en el mapa...');
      setListaOptimizada(order);
      setRutaGeoJSON(trip.geometry);

      // Pintar en mapa
      const layer = L.geoJSON(trip.geometry, { style: { color: '#2563eb', weight: 5 } });
      layer.addTo(mapRef.current);
      routeLayerRef.current = layer;
      
      // Marcadores
      const origenMarker = L.marker([origen.lat, origen.lng]).addTo(mapRef.current);
      origenMarker.bindTooltip('Origen').openTooltip();
      markersRef.current.push(origenMarker);
      
      destinos.forEach((d, idx) => {
        const label = order.find(o => (o.pedidoId === (d.pedido.pedidoId || d.pedido.id)))?.orden || idx + 1;
        const m = L.marker([d.lat, d.lng]).addTo(mapRef.current);
        m.bindTooltip(`#${label}`).openTooltip();
        markersRef.current.push(m);
      });
      
      mapRef.current.fitBounds(layer.getBounds(), { padding: [20, 20] });
      
      // Mostrar mensaje de éxito
      setEstadoCalculo('¡Ruta calculada exitosamente!');
      if (erroresGeocodificacion.length > 0) {
        alert(`Ruta calculada exitosamente con ${destinos.length} direcciones válidas.\n\nSe omitieron ${erroresGeocodificacion.length} direcciones con problemas.`);
      }
      
    } catch (error) {
      console.error('Error calculando ruta:', error);
      
      // Mensajes de error más específicos
      let mensajeError = 'Ocurrió un error al calcular la ruta.';
      
      if (error.message.includes('conexión') || error.message.includes('network')) {
        mensajeError = 'Error de conexión. Verifica tu internet e intenta nuevamente.';
      } else if (error.message.includes('dirección') || error.message.includes('geocodificación')) {
        mensajeError = 'Error con las direcciones. Verifica que las direcciones sean correctas y completas.';
      } else if (error.message.includes('servicio') || error.message.includes('OSRM')) {
        mensajeError = 'El servicio de rutas está temporalmente no disponible. Intenta más tarde.';
      } else {
        mensajeError = `Error: ${error.message}`;
      }
      
      alert(mensajeError);
    } finally {
      setCalculando(false);
      setEstadoCalculo('');
    }
  }

  async function guardarRuta() {
    if (!rutaGeoJSON || listaOptimizada.length === 0) return;
    const datos = {
      origen: origenTexto || origenCoords || center,
      listaOptimizada,
      geometria: rutaGeoJSON,
      estado: 'generada'
    };
    const saved = await RutasService.guardarRuta(datos);
    if (onRutaGuardada) onRutaGuardada(saved);
    alert('Ruta guardada');
  }

  const compact = modo === 'compacto';

  return (
    <div>
      {!compact && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div>
            <h3 style={{ margin:0 }}>Ruta optimizada</h3>
            {estadoCalculo && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {estadoCalculo}
              </div>
            )}
          </div>
          <div>
            <button className="btn btn-primary" onClick={calcularRuta} disabled={calculando}>
              {calculando ? 'Calculando...' : 'Calcular ruta'}
            </button>
            <button className="btn btn-success" style={{ marginLeft:8 }} onClick={guardarRuta} disabled={listaOptimizada.length === 0}>Guardar</button>
            {onClose && <button className="btn btn-secondary" style={{ marginLeft:8 }} onClick={onClose}>Cerrar</button>}
          </div>
        </div>
      )}

      <div ref={mapContainerRef} style={{ ...containerStyle, height: compact ? 240 : 400 }} />

      {!compact && (
        <div style={{ marginTop:12 }}>
          <h4 style={{ margin:'12px 0 8px' }}>Entregas en orden optimizado</h4>
          <ol>
            {listaOptimizada.map(item => (
              <li key={item.pedidoId}>{item.orden}. {item.direccion}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

